import { useCart } from "@/context/CartContext";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { toast } from "sonner";
import { useState } from "react";

export function CartDrawer() {
  const { items, updateQuantity, removeItem, total, isOpen, setIsOpen } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, option: i.option, selections: i.selections })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={() => setIsOpen(false)} data-testid="cart-overlay" />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md animate-slide-in-right flex-col border-l border-white/10 bg-[#0B0F17]" data-testid="cart-drawer">
        <div className="flex items-center justify-between border-b border-white/10 p-6">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Your Cart</h2>
          <button onClick={() => setIsOpen(false)} className="rounded-sm p-1 text-slate-400 hover:text-white" data-testid="close-cart">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <ShoppingCartIcon className="mb-4 h-12 w-12 text-slate-600" />
              <p className="font-mono text-sm uppercase tracking-widest text-slate-500" data-testid="cart-empty-message">Your cart is empty</p>
              <button
                onClick={() => { setIsOpen(false); navigate("/shop"); }}
                className="mt-6 bg-volt px-6 py-3 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white"
                data-testid="cart-shop-now"
              >
                Shop Parts
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.product_id} className="flex gap-4 border border-white/10 bg-surface p-4" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.image_url} alt={item.name} className="h-20 w-20 object-cover" />
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <h3 className="font-display font-bold uppercase text-white">{item.name}</h3>
                      {item.option && <p className="font-mono text-xs uppercase tracking-widest text-slate-400" data-testid={`cart-option-${item.product_id}`}>{item.option}</p>}
                      <p className="font-mono text-sm text-volt">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="rounded-sm border border-white/10 p-1 text-slate-400 hover:border-volt hover:text-volt" data-testid={`cart-decrease-${item.product_id}`}>
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="font-mono text-sm text-white" data-testid={`cart-quantity-${item.product_id}`}>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="rounded-sm border border-white/10 p-1 text-slate-400 hover:border-volt hover:text-volt" data-testid={`cart-increase-${item.product_id}`}>
                        <Plus className="h-3 w-3" />
                      </button>
                      <button onClick={() => removeItem(item.product_id)} className="ml-auto rounded-sm p-1 text-slate-500 hover:text-red-400" data-testid={`cart-remove-${item.product_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 p-6">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-sm uppercase tracking-widest text-slate-400">Subtotal</span>
              <span className="font-display text-2xl font-bold text-white" data-testid="cart-total">${total.toFixed(2)}</span>
            </div>
            <button
              onClick={checkout}
              disabled={loading}
              className="w-full bg-volt py-4 font-mono text-sm font-bold uppercase tracking-widest text-obsidian transition-colors hover:bg-white disabled:opacity-50"
              data-testid="checkout-button"
            >
              {loading ? "Processing..." : "Checkout"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function ShoppingCartIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
    </svg>
  );
}
